import { useState } from 'react'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { selectCrossClientLandingSelectedOwnerId } from '../../../../store/crossClient/crossClientLanding'
import { useGetCrossClientQueryExecutionsQuery } from '../../../../store/crossClient/crossClient'
import { type CrossClientQueryExecution, CrossClientQueryPhases } from '../../../../models/CrossClient'

const NEEDS_ACTION_PHASES = [CrossClientQueryPhases.INITIALIZED, CrossClientQueryPhases.COMPLETED]
const PAGE_SIZE = 10

export interface CrossClientNeedsActionGroups {
  // Group A — matches found that still need an action.
  matchesWaiting: CrossClientQueryExecution[]
  matchesWaitingCount: number
  matchesWaitingHasMore: boolean
  matchesWaitingLoadingMore: boolean
  showMoreMatchesWaiting: () => void
  // Group B — no unactioned matches (none found yet, or all actioned) and clients remain to analyze.
  continueToFind: CrossClientQueryExecution[]
  continueToFindCount: number
  continueToFindHasMore: boolean
  continueToFindLoadingMore: boolean
  showMoreContinueToFind: () => void
  totalCount: number
  loading: boolean
}

export const useCrossClientNeedsActionGroups = (): CrossClientNeedsActionGroups => {
  const accountId = useAppSelector(selectAccountId)
  const selectedOwnerId = useAppSelector(selectCrossClientLandingSelectedOwnerId)
  const createdBy = selectedOwnerId ?? undefined

  // Page sizes are scoped to the owner they were expanded for. Deriving the effective size (rather than
  // resetting via a render-phase setState or an effect) keeps a new owner at PAGE_SIZE while avoiding
  // the cascading-render pitfalls that break react-redux's store subscription.
  const [paging, setPaging] = useState({ ownerId: selectedOwnerId, matches: PAGE_SIZE, continueTo: PAGE_SIZE })
  const sameOwner = paging.ownerId === selectedOwnerId
  const matchesWaitingSize = sameOwner ? paging.matches : PAGE_SIZE
  const continueToFindSize = sameOwner ? paging.continueTo : PAGE_SIZE
  const showMore = (key: 'matches' | 'continueTo') =>
    setPaging((prev) => {
      const base = prev.ownerId === selectedOwnerId ? prev : { ownerId: selectedOwnerId, matches: PAGE_SIZE, continueTo: PAGE_SIZE }
      return { ...base, [key]: base[key] + PAGE_SIZE }
    })

  const groupA = useGetCrossClientQueryExecutionsQuery(
    {
      accountId,
      phases: NEEDS_ACTION_PHASES,
      hasUnactionedMatches: true,
      expand: ['matches'],
      createdBy,
      pageRequest: { page: 0, size: matchesWaitingSize },
    },
    { refetchOnMountOrArgChange: true },
  )

  const groupB = useGetCrossClientQueryExecutionsQuery(
    {
      accountId,
      phases: NEEDS_ACTION_PHASES,
      hasUnactionedMatches: false,
      fullyAnalyzed: false,
      expand: ['progress'],
      createdBy,
      pageRequest: { page: 0, size: continueToFindSize },
    },
    { refetchOnMountOrArgChange: true },
  )

  const matchesWaiting = groupA.data?.data ?? []
  const continueToFind = groupB.data?.data ?? []
  const matchesWaitingCount = groupA.data?.page.totalElements ?? 0
  const continueToFindCount = groupB.data?.page.totalElements ?? 0

  return {
    matchesWaiting,
    matchesWaitingCount,
    matchesWaitingHasMore: matchesWaiting.length < matchesWaitingCount,
    matchesWaitingLoadingMore: groupA.isFetching,
    showMoreMatchesWaiting: () => showMore('matches'),
    continueToFind,
    continueToFindCount,
    continueToFindHasMore: continueToFind.length < continueToFindCount,
    continueToFindLoadingMore: groupB.isFetching,
    showMoreContinueToFind: () => showMore('continueTo'),
    // Groups partition on hasUnactionedMatches → disjoint, so totals sum without double-counting.
    totalCount: matchesWaitingCount + continueToFindCount,
    loading: groupA.isLoading || groupB.isLoading,
  }
}
